import { C, MCS_CONSTANT } from '../data/physicsConstants';

// ============================================
// PHYSICS CALCULATIONS (Pure Functions)
// ============================================

export const calculateKinematics = (momentum, mass) => {
    // E² = p² + m²
    const energy = Math.sqrt(momentum * momentum + mass * mass);
    // β = v/c = p/E
    const beta = momentum / energy;
    // γ = E/m = 1/√(1-β²)
    const gamma = energy / mass;
    const kineticEnergy = energy - mass;
    return { energy, beta, gamma, kineticEnergy };
};

export const calculateDecayLength = (beta, gamma, lifetime) => {
    // λ = βcγτ₀
    // Lifetime in seconds, C in m/s, returns meters
    if (lifetime === Infinity) return Infinity;
    return beta * C * gamma * lifetime;
};

export const calculateLabLifetime = (gamma, properLifetime) => {
    // τ_lab = γ × τ₀
    if (properLifetime === Infinity) return Infinity;
    return gamma * properLifetime;
};

// ============================================
// MCS PHYSICS CALCULATIONS (Highland Formula)
// ============================================
export const calculateMCSAngle = (momentum, mass, thickness, X0) => {
    // Highland formula: θ_RMS = (13.6 MeV / βcp) × √(L/X₀) × [1 + 0.038 ln(L/X₀)]
    // momentum in GeV/c, thickness in cm, X0 in cm
    // Returns angle in radians
    
    if (!X0 || X0 <= 0 || thickness <= 0) return 0;

    const { beta } = calculateKinematics(momentum, mass);
    const betaP = beta * momentum; // βp in GeV/c (momentum is GeV/c)
    // Formula uses MeV for constant 13.6, so p should be MeV/c?
    // The constant 13.6 MeV. 
    // θ = (13.6 MeV / βcp) ... 
    // If p is GeV/c, then p * 1000 = MeV/c.
    // So (13.6 / (beta * p * 1000)) = (0.0136 / (beta * p)).
    
    // Check previous implementation in Context:
    // const betaP = beta * momentum; // βp in GeV/c
    // const thetaRMS = (0.0136 / betaP) * Math.sqrt(ratio) * logCorrection;
    // Yes, 0.0136 corresponds to using GeV/c for p.

    const ratio = thickness / X0;
    const logCorrection = 1 + 0.038 * Math.log(ratio);
    const thetaRMS = (0.0136 / betaP) * Math.sqrt(ratio) * logCorrection;
    return thetaRMS; // radians
};

export const calculateMCSVariance = (momentum, mass, thickness, X0) => {
    // Variance = θ_RMS²
    const theta = calculateMCSAngle(momentum, mass, thickness, X0);
    return theta * theta;
};

export const extractRadiationLength = (momentum, mass, thickness, measuredTheta) => {
    // Inverse Highland formula to extract X0 from measured θ_RMS
    // Iterative solution needed due to log term
    const { beta } = calculateKinematics(momentum, mass);
    const betaP = beta * momentum;

    // Initial guess: ignore log correction
    let X0 = thickness * Math.pow(0.0136 / (betaP * measuredTheta), 2);

    // Newton-Raphson iteration
    for (let i = 0; i < 10; i++) {
        const ratio = thickness / X0;
        const logCorr = 1 + 0.038 * Math.log(ratio);
        const thetaCalc = (0.0136 / betaP) * Math.sqrt(ratio) * logCorr;
        const dTheta = measuredTheta - thetaCalc;
        if (Math.abs(dTheta) < 1e-8) break;
        X0 *= Math.pow(thetaCalc / measuredTheta, 2);
    }
    return X0;
};
